import { Button, ButtonGroup, Card, Grid, GridColumn, GridRow, Table, type TableColumns } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { getTokens } from '../../../utils/authStore';

const OrganizationsSubpagePageQueryDocument = graphql(`
  query OrganizationsSubpagePageData {
    pimOrganizations {
      uuid
      __typename
      name
      bpEnabled
    }
  }
`);

type OrganizationsListDataType = {
  uuid: string;
  name: string;
  bpEnabled: boolean;
};

export const OrganizationsSubpage: FC = () => {
  const { t } = useTranslation();

  const [returnLog, setReturnLog] = useState<string>('');
  const [running, setRunning] = useState<boolean>(false);

  const [{ data }, refetchOrganizations] = useQuery({
    query: OrganizationsSubpagePageQueryDocument,
  });

  const organizations: OrganizationsListDataType[] = useMemo(() => {
    return (
      data?.pimOrganizations.map((o) => {
        return {
          ...o,
          bpEnabled: o.bpEnabled ?? false,
        };
      }) ?? []
    );
  }, [data]);

  const createPersonColumns = (): TableColumns<OrganizationsListDataType>[] => {
    return [
      {
        header: t('common.name'),
        accessorKey: 'name',
        id: 'name',
        size: 250,
        canExpand: true,
      },
      {
        header: t('common.uuid'),
        accessorKey: 'uuid',
        id: 'uuid',
        size: 100,
      },
      {
        header: t('app.title.short'),
        accessorKey: 'bpEnabled',
        type: 'boolean',
        id: 'bpEnabled',
      },
    ];
  };

  const tableColumns = useMemo(createPersonColumns, [t]);

  const memoizedData = useMemo((): OrganizationsListDataType[] => {
    return data ? organizations : [];
  }, [data, organizations]);

  const handleSetup = async (row: OrganizationsListDataType) => {
    await callActionAndHandleResult(
      `${import.meta.env.VITE_APP_BACKEND_URI}${import.meta.env.VITE_APP_BACKEND_REST_PATH}/setup/${row.uuid}`,
      JSON.stringify({
        uuid: row.uuid,
        matrix: true,
      }),
    );
  };

  const callActionAndHandleResult = async (url: string, body?: BodyInit | null | undefined) => {
    setRunning(true);
    setReturnLog('');
    const tokens = await getTokens();
    const result = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens?.access_token}`,
        'content-type': 'application/json',
      },
      body: body,
    });

    setReturnLog(JSON.stringify(await result.json(), null, 2));
    refetchOrganizations({ requestPolicy: 'network-only' });
    setRunning(false);
  };

  return (
    <BpSubpage>
      <Grid>
        <GridRow>
          <GridColumn width={9}>
            <Table<OrganizationsListDataType>
              showBorderRadius
              showShadow
              canScroll
              minHeight={600}
              isDesktop={true}
              isOnWhite={false}
              columns={tableColumns}
              data={memoizedData}
              showActionBar
              actionBarSettings={{
                showExpertFilter: false,
                showAddButton: false,
                showBulkEdit: false,
                showPrintButton: false,
              }}
              lastColWidth='80px'
              lastCol={(row) => {
                return (
                  <ButtonGroup>
                    <Button
                      hierarchy='secondary'
                      type='button'
                      disabled={row.original.bpEnabled || running}
                      onClick={async () => {
                        await handleSetup(row.original);
                      }}
                    >
                      Setup
                    </Button>
                  </ButtonGroup>
                );
              }}
            />
          </GridColumn>
          <GridColumn width={3}>
            <Card>
              <pre>{returnLog}</pre>
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>
    </BpSubpage>
  );
};
